import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import ExportLabel from '../../../components/common/ExportLabel/Export';
import './Countries.css';
import InputBoxLarge from '../../../components/common/InputBox/InputBoxLarge';
import InputBoxSmall from '../../../components/common/InputBox/InputBoxSmall';
import SearchSelectBoxDrown from '../../../components/common/SelectBox/searchSelect';
import UploadBoxCountry from '../../../components/common/InputBox/UploadCountry';
import SearchSelectBox from '../../../components/common/SelectBox/SelectBoxWithSearch';
import SearchSelectTimezone from '../../../components/common/SelectBox/SelectBoxWithSearchTimezone';
import ErrorNotification from '../../../components/common/AlertBox/ErrorNotification';
import { SuccessAlert } from '../../../components/common/AlertBox/GreenAlert';
import authenticatedAxios from '../../../authenticatedAxios';
import { baseurl } from '../../../api';
import { isRequired, isTextOnly, isValidCountryCode } from '../../../validation';
import useRefreshStore from '../../../store/useRefreshStore';

const CountryAdd = () => {
  const navigate = useNavigate();
  const { triggerRefresh } = useRefreshStore();
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [activePlanOptions, setActivePlanOptions] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    name: '',
    iso_code: '',
    country_code: '',
    currency: '',
    country_flag: null,
    plans: [],
    timezone: [],
  });

  useEffect(() => {
    fetchCurrencyOptions();
    fetchAllActivePlans();
    fetchTimezoneOptions();
  }, []);

  const fetchCurrencyOptions = async () => {
    try {
      const response = await authenticatedAxios.get(`${baseurl}currency/active/`);
      if (response.data && response.data.response_code === 1) {
        const options = (response.data.data || []).map((currency) => ({
          label: currency.name ? `${currency.name}${currency.symbol ? ` (${currency.symbol})` : ''}` : currency.symbol,
          value: currency.id,
        }));
        setCurrencyOptions(options);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
  };

  const fetchAllActivePlans = async () => {
    try {
      const response = await authenticatedAxios.get(`${baseurl}plans/active/`);
      if (response.data && response.data.response_code === 1) {
        const options = (response.data.data || []).map((plan) => ({
          label: plan.plan_name || plan.display_name,
          value: plan.id,
          id: plan.id,
        }));
        setActivePlanOptions(options);
      }
    } catch (error) {
      console.error('Error fetching active plans:', error);
    }
  };

  const fetchTimezoneOptions = async () => {
    try {
      const { data } = await authenticatedAxios.get(`${baseurl}localization/timezones/`);
      if (data && data.response_code === 1) {
        const options = (data.data || []).map((tz) => ({
          label: `${tz.name} (${tz.timezone})`,
          value: tz.id,
          id: tz.id,
        }));
        setTimezoneOptions(options);
      }
    } catch (err) {
      console.error('Error fetching timezones:', err);
    }
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.name || !form.name.trim()) {
      newErrors.name = ['Country Name is required'];
    } else {
      const textOnly = isTextOnly(form.name);
      if (textOnly !== true) {
        newErrors.name = [textOnly];
      }
    }

    if (!form.iso_code || !form.iso_code.trim()) {
      newErrors.iso_code = ['ISO Code is required'];
    }

    if (!form.country_code || !form.country_code.trim()) {
      newErrors.country_code = ['Country Code is required'];
    } else {
      const codeCheck = isValidCountryCode(form.country_code);
      if (codeCheck !== true) {
        newErrors.country_code = [codeCheck];
      }
    }

    if (!form.currency) {
      newErrors.currency = ['Currency is required'];
    }

    if (!form.country_flag) {
      newErrors.country_flag = ['Country Flag is required'];
    }

    return newErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const formData = new FormData();
    formData.append('name', form.name.trim());
    formData.append('iso_code', form.iso_code.trim());
    formData.append('country_code', form.country_code.trim());
    formData.append('currency', form.currency);

    if (form.country_flag) {
      formData.append('country_flag', form.country_flag);
    }

    if (Array.isArray(form.plans)) {
      form.plans.forEach((plan) => {
        formData.append('plans', plan.value || plan.id);
      });
    }

    if (Array.isArray(form.timezone)) {
      form.timezone.forEach((tz) => {
        formData.append('timezones', tz.value || tz.id);
      });
    }

    try {
      const response = await authenticatedAxios.post(
        `${baseurl}localization/country/`,
        formData
      );

      if (response.data && response.data.response_code === 1) {
        triggerRefresh('Countries');
        SuccessAlert('Country created successfully');
        const createdId = response.data.data?.id;
        if (createdId) {
          navigate(`/countries/detail/${createdId}`);
        } else {
          navigate('/countries');
        }
      } else {
        const msg = response.data?.message;
        if (msg && typeof msg === 'object') {
          setErrors(msg);
        } else {
          setErrors({ general: msg || 'Failed to add country.' });
        }
      }
    } catch (error) {
      console.error('Error creating country:', error);
      if (error.response?.data?.message) {
        const msg = error.response.data.message;
        if (typeof msg === 'object') {
          setErrors(msg);
        } else {
          setErrors({ general: msg });
        }
      } else {
        setErrors({ general: 'Failed to add country. Please try again.' });
      }
    }
  };

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader
          pageName="Localization / Countries /"
          nestedpageName="Create"
          linkTo="/countries"
        />
        <div className="export-container">
          <ExportLabel />
        </div>
      </div>

      <div className="page-container">
        <ErrorNotification errors={errors} marginBottom="16px" />

        {/* Country Details Card */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Country Details</span>
            <button
              className="popup-add-button common-text white fts-15 ftw-5"
              onClick={handleSubmit}
            >
              Add Country
            </button>
          </div>

          <div className="card-body">
            <InputBoxLarge
              label="Country Name"
              defaultValue={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              isRequired
              error={errors.name}
            />

            <div className="row-2">
              <InputBoxSmall
                label="ISO Code"
                defaultValue={form.iso_code}
                onChange={(e) => handleChange('iso_code', e.target.value)}
                isRequired
                error={errors.iso_code}
              />
              <InputBoxSmall
                label="Country Code"
                defaultValue={form.country_code}
                onChange={(e) => handleChange('country_code', e.target.value)}
                isRequired
                error={errors.country_code}
              />
            </div>

            <SearchSelectBoxDrown
              label="Currency"
              options={currencyOptions}
              defaultValue={form.currency}
              onChange={(e) => handleChange('currency', e.target.value)}
              isRequired
              error={errors.currency}
            />

            <UploadBoxCountry
              label="Upload Flag"
              isRequired
              onFileChange={(file) => handleChange('country_flag', file)}
              error={errors.country_flag}
            />
          </div>
        </div>

        {/* Active Plans Card */}
        <div className="features-card">
          <SearchSelectBox
            label="Choose Active Plans"
            allPlans={activePlanOptions}
            defaultSelectedOptions={form.plans}
            onChange={(selected) => handleChange('plans', selected)}
            error={errors.plans}
          />
        </div>

        {/* Timezones Card */}
        <div className="features-card">
          <SearchSelectTimezone
            label="Choose Timezones"
            allTimezones={timezoneOptions}
            defaultSelectedOptions={form.timezone}
            onChange={(selected) => handleChange('timezone', selected)}
            error={errors.timezone}
          />
        </div>
      </div>
    </div>
  );
};

export default CountryAdd;
